import dayjs from 'dayjs';
import { ActionTypesEnum } from '@api/ringil-api.v2.tsx';
import type { GetExternalOrdersResponse, ReservationActionTypesEnum, ReservationDetailOutDTO, UpdateOrdersAndTimeslotRequest } from '@api/ringil-api.v2.tsx';
import { isExternalIdPresent, Order } from '@components/framework/orders/common.tsx';
import { apiPragueFromDayjsInTz, apiUtcFromDayjs, dayjsFromApiUtcInTz, dayjsFromDayAndTimeStrings, stringHHmmFromStringInTz } from '@utils/dateWithTz.tsx';
import { isNotNullOrUndefined } from '@utils/utils.tsx';

/**
 * RD-3062 — the form ↔ API mapping of `EditReservationOrders`, moved out of the component unchanged so it
 * can be unit-tested. The component keeps the hooks, API calls and UI; everything here is pure.
 */

export interface EditReservationOrdersForm {
  actionTypes: ReservationActionTypesEnum[];
  orders: Order[];
  companyLocationId: string;
  rampId: string;
  localDate: string;
  preferredDate?: string;
  durationInMin: number;
  arrivalHourString: string;
  timezoneId: string;
}

/** The timeslot fields the availability check is debounced on. */
export interface EditReservationOrdersTimeslotState {
  rampId: string;
  durationInMin: number;
  arrivalHourString: string;
  localDate: string;
  timezoneId?: string;
}

/** The form a loaded reservation starts from; a reservation without orders gets one empty order. */
export const editReservationOrdersFormFromDetail = (d: ReservationDetailOutDTO): EditReservationOrdersForm => ({
  actionTypes: d.reservationActionTypes,
  orders:
    d.orders.length > 0
      ? d.orders.map(
          t =>
            ({
              id: t.id,
              orderName: t.orderName,
              orderValue: t.orderValue,
              orderAmount: t.orderAmount,
              orderDescription: t.orderDescription,
              orderNote: t.orderNote,
              eanCodes: t.loadedEans,
              externalOrders: t.externalData?.map(eo => ({
                orderNumber: eo.orderNumber,
                timeFrom: eo.timeFrom,
                timeTo: eo.timeTo,
              })),
              items: t.items.map(i => ({
                amount: i.amount,
                cargoTemplateCode: i.cargoTemplate?.code,
                stackable: i.stackable,
                itemNote: i.itemNote,
                decimalPlaces: i.cargoTemplate?.decimalPlaces,
                height: i.height,
                width: i.width,
                length: i.length,
                weight: i.weight,
              })),
            } as Order),
        )
      : [
          {
            items: [],
            orderAmount: null,
            orderDescription: null,
            orderName: null,
            orderNote: null,
            orderValue: null,
          },
        ],
  localDate: d.slot?.localDate ?? d.preferredDate ?? dayjs().format('YYYY-MM-DD'),
  rampId: d.rampId,
  arrivalHourString: d.slot?.utcStart ? stringHHmmFromStringInTz(d.slot.utcStart, d.slot?.timezoneId ?? 'Europe/Prague') : undefined,
  durationInMin: d.slot?.durationInMin ? Math.floor(d.slot?.durationInMin) : 30,
  companyLocationId: d.locationId,
  preferredDate: d.preferredDate,
  timezoneId: d.slot?.timezoneId ?? 'Europe/Prague',
});

/** One order as the update, the slot check and the free-slot search send it. */
export const mapOrder = (order: Order, externalOrdersResponse?: GetExternalOrdersResponse) => {
  return {
    id: order.id,
    orderName: order.orderName,
    orderValue: order.orderValue,
    cargoAmount: order.orderAmount,
    cargoDescription: order.orderDescription,
    orderNote: order.orderNote,
    externalOrders: isExternalIdPresent(externalOrdersResponse) ? order.externalOrders : undefined,
    items: order.items.map(i => ({
      amount: i.amount,
      cargoTemplateCode: i.cargoTemplateCode,
      stackable: i.stackable,
      itemNote: i.itemNote,
      decimalPlaces: i.decimalPlaces,
      height: i.height,
      width: i.width,
      length: i.length,
      weight: i.weight,
    })),
  };
};

/** The body of `updateReservationOrdersAndTimeslot`. */
export const updateReservationOrdersRequest = (data: EditReservationOrdersForm, externalOrdersResponse?: GetExternalOrdersResponse): UpdateOrdersAndTimeslotRequest => ({
  orders: data.orders.map(order => mapOrder(order, externalOrdersResponse)),
  actionTypes: data.actionTypes.map(t => ActionTypesEnum[t]),
  timeslot: {
    rampId: data.rampId,
    localDate: data.localDate,
    sinceHourString: data.arrivalHourString,
    durationInMinutes: data.durationInMin,
  },
});

/** Whether every order's items are complete enough to check the slot (an order without items passes). */
export const isValidItems = (orders: Order[]) => (
  Array.isArray(orders) &&
  orders.every(o =>
      !o.items
      || o.items.length === 0
      || (Array.isArray(o.items) &&
        o.items.length > 0 &&
        o.items.every(i =>
          isNotNullOrUndefined(i.amount) &&
          isNotNullOrUndefined(i.cargoTemplateCode) &&
          isNotNullOrUndefined(i.stackable),
        )
      ),
  )
);

/** Whether the picked slot already started — such slots are not checked. */
export const isTimeslotInPast = (timeslotState: any) => {
  if (!timeslotState) return false;
  const since = dayjsFromDayAndTimeStrings(timeslotState.localDate, timeslotState.arrivalHourString, timeslotState.timezoneId ?? 'Europe/Prague');
  return since.isBefore(dayjs());
};

/** The timeslot the availability check sends for the edited orders' slot. */
export const editOrdersTimeslotForReservationCheck = (debouncedTimeslotState: EditReservationOrdersTimeslotState) => {
  const tz = debouncedTimeslotState.timezoneId ?? 'Europe/Prague';
  const sinceDayjs = dayjsFromDayAndTimeStrings(debouncedTimeslotState.localDate, debouncedTimeslotState.arrivalHourString, tz);
  const tillDayjs = sinceDayjs.add(debouncedTimeslotState.durationInMin, 'minute');
  return {
    since: apiPragueFromDayjsInTz(dayjsFromApiUtcInTz(sinceDayjs.toISOString(), tz), tz),
    till: apiPragueFromDayjsInTz(dayjsFromApiUtcInTz(tillDayjs.toISOString(), tz), tz),
    utcStart: apiUtcFromDayjs(sinceDayjs),
    utcEnd: apiUtcFromDayjs(tillDayjs),
    timezoneId: tz,
  };
};
